import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, Injector, Input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, first, firstValueFrom, interval, map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  iconoirMenu,
  iconoirBell,
  iconoirClock,
  iconoirBellNotification,
  iconoirCheck,
  iconoirCheckCircle,
  iconoirTrash,
  iconoirNavArrowRight,
  iconoirChatBubble,
  iconoirCalendar,
  iconoirUser,
  iconoirEdit,
  iconoirPackage,
  iconoirCursorPointer,
  iconoirSettings,
  iconoirCash,
  iconoirHalfMoon,
  iconoirFlash,
  iconoirFlashOff,
  iconoirSunLight,
  iconoirNavArrowDown,
  iconoirMoreHoriz,
  iconoirEye,
  iconoirPlus,
  iconoirChatLines,
  iconoirArrowLeft,
} from '@ng-icons/iconoir';
import { NotificationService } from '../../../../core/notification.service';
import { Notification } from '../../../../models/interfaces';
import { NotificationModule } from '../../../../models/enums';
import { VoiceContextService, VoiceOverlayService } from '../../../../core/voice';
import { SubscriptionFeaturesService } from '../../../../services/subscription-features.service';
import { BreadcrumbService, currentPageLabel, parentCrumb } from '../../../../services/breadcrumb.service';
import { HeaderContentService } from '../../../../services/header-content.service';
import { HeaderSearchService } from '../../../../services/header-search.service';
import { SubNavService, SubNavItem, SubNavChild } from '../../../../services/sub-nav.service';
import { UndoDeleteService } from '../../../../services/undo-delete.service';
import { DesignService } from '../../../../services/design.service';
import { OrganisationLogoService } from '../../../../services/organisation-logo.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { NgTemplateOutlet } from '@angular/common';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';

import { MotionPreferenceService } from '../../../../services/motion-preference.service';
import { TooltipDirective } from '../../tooltip/tooltip.directive';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgIcon, RouterLink, RouterLinkActive, NgTemplateOutlet, BreadcrumbComponent, TooltipDirective],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)'
  },
  viewProviders: [
    provideIcons({
      iconoirMenu,
      iconoirBell,
      iconoirClock,
      iconoirBellNotification,
      iconoirCheck,
      iconoirCheckCircle,
      iconoirTrash,
      iconoirNavArrowRight,
      iconoirChatBubble,
      iconoirCalendar,
      iconoirUser,
      iconoirEdit,
      iconoirPackage,
      iconoirCursorPointer,
      iconoirSettings,
      iconoirCash,
      iconoirHalfMoon,
      iconoirFlash,
      iconoirFlashOff,
      iconoirSunLight,
      iconoirNavArrowDown,
      iconoirMoreHoriz,
      iconoirEye,
      iconoirPlus,
      iconoirChatLines,
      iconoirArrowLeft,
    })
  ]
})
export class HeaderComponent implements OnInit {
  @Input() sidebarMode: 'expanded' | 'collapsed' | 'closed' = 'expanded';
  menuToggle = output<void>();

  /** Réglage « animations avancées » (bouton à côté du thème). */
  protected readonly motion = inject(MotionPreferenceService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private headerContentService = inject(HeaderContentService);
  private headerSearchService = inject(HeaderSearchService);
  private subNavService = inject(SubNavService);
  private undoDeleteService = inject(UndoDeleteService);
  public authService = inject(AuthService);
  public designService = inject(DesignService);
  private voiceContext = inject(VoiceContextService);
  private voiceOverlay = inject(VoiceOverlayService);
  readonly subFeatures = inject(SubscriptionFeaturesService);
  readonly orgLogos = inject(OrganisationLogoService);
  private readonly breadcrumbs = inject(BreadcrumbService);

  /** Mobile : le bouton de navigation porte le nom de la page courante (dernier maillon du fil). */
  readonly pageLabel = computed(() => currentPageLabel(this.breadcrumbs.trail()) ?? 'Navigation');
  /** Mobile : bouton « ← parent » vers l'avant-dernier maillon du fil d'Ariane. */
  readonly parentLink = computed(() => parentCrumb(this.breadcrumbs.trail()));

  /** Logo de l'entreprise (MOKA-342) ; une image en échec n'est plus affichée. */
  readonly orgLogoFailed = signal<string | null>(null);
  readonly orgLogoUrl = computed(() => {
    const url = this.orgLogos.themedUrl();
    return url && url !== this.orgLogoFailed() ? url : null;
  });

  showDropdown = signal(false);
  openSubMenu = signal<string | null>(null);

  toggleSubMenu(label: string): void {
    this.openSubMenu.update(v => v === label ? null : label);
  }

  closeSubMenu(): void {
    this.openSubMenu.set(null);
  }

  isRouteActive(routerLink: string | string[]): boolean {
    const link = Array.isArray(routerLink) ? routerLink.join('/') : routerLink;
    return this.router.url.startsWith(link);
  }

  /** Menu déroulant actif : sa propre route, ou celle de l'une de ses entrées (menus regroupés). */
  isParentActive(item: SubNavItem): boolean {
    if (item.active) return item.active();
    if (item.routerLink && this.isRouteActive(item.routerLink)) return true;
    return (item.children ?? []).some(child => !!child.routerLink && this.isRouteActive(child.routerLink));
  }

  /** « + Ajouter » d'une entrée de menu : ferme le menu (bureau ou mobile) puis lance la création. */
  runQuickAdd(quickAdd: { action: () => void }): void {
    this.closeSubMenu();
    this.closeMobileNav();
    quickAdd.action();
  }

  navigateChild(child: { routerLink?: string | string[]; queryParams?: Record<string, string> }): void {
    if (!child.routerLink) return;
    const link = Array.isArray(child.routerLink) ? child.routerLink : [child.routerLink];
    this.router.navigate(link, { queryParams: child.queryParams || {} });
    this.closeSubMenu();
  }

  headerContent = this.headerContentService.headerContent;
  headerSearch = this.headerSearchService.searchContent;

  // Sub-navigation from service
  subNav = this.subNavService.subNav;

  visibleItems = computed<SubNavItem[]>(() => {
    const nav = this.subNav();
    if (!nav) return [];
    const max = nav.maxVisible ?? 5;
    return nav.items.length > max ? nav.items.slice(0, max) : nav.items;
  });

  overflowItems = computed<SubNavItem[]>(() => {
    const nav = this.subNav();
    if (!nav) return [];
    const max = nav.maxVisible ?? 5;
    return nav.items.length > max ? nav.items.slice(max) : [];
  });

  showMobileNav = signal(false);
  showOverflow = signal(false);

  toggleOverflow(): void {
    this.showOverflow.update(v => !v);
  }

  toggleMobileNav(): void {
    this.showMobileNav.update(v => !v);
  }

  closeMobileNav(): void {
    this.showMobileNav.set(false);
  }

  onMobileNavItemClick(item: SubNavItem | SubNavChild): void {
    if (item.action) {
      item.action();
    } else if (item.routerLink) {
      const link = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      const queryParams = 'queryParams' in item ? item.queryParams : undefined;
      this.router.navigate(link, { queryParams: queryParams || {} });
    }
    this.closeMobileNav();
  }

  closeOverflow(): void {
    this.showOverflow.set(false);
  }

  onOverflowItemClick(item: SubNavItem): void {
    if (item.action) {
      item.action();
    } else if (item.routerLink) {
      const link = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(link);
    }
    this.closeOverflow();
  }

  // Détecte si on est sur une page calendar
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  isCalendarPage = computed(() => this.currentUrl().startsWith('/calendar'));
  /** Accueil : le fil d'Ariane n'y a qu'un maillon, la deuxième ligne serait vide. */
  isHomePage = computed(() => this.currentUrl().split(/[?#]/)[0] === '/dashboard');
  isVoiceEnabled = this.voiceContext.isVoiceEnabled;

  /**
   * Vrai une fois la première page affichée. Avant, la deuxième ligne reste réservée
   * (classe `is-pending`) au lieu d'être masquée : les pages posent recherche et actions
   * dans leur `ngAfterViewInit`, après l'en-tête, et la ligne qui apparaissait alors
   * repoussait tout le contenu de 57 px — un saut mesuré sur chaque page en mobile
   * (MOKA-366). Seules les pages sans action la voient se replier, une fois.
   */
  protected readonly pageSettled = signal(false);

  /**
   * Pages qui posent une recherche ou des actions dans la deuxième ligne, connues dès
   * l'adresse (la page n'est pas encore chargée au premier affichage) : les modules dont
   * le layout les pose pour toutes leurs pages, et deux accueils. Réserver la ligne
   * ailleurs ne ferait que déplacer le saut (la ligne se replierait). Une adresse oubliée
   * coûte un saut au premier affichage, rien de plus.
   */
  protected readonly secondaryExpectedAtStart = expectsSecondaryRow(window.location.pathname);

  /** L'accueil, reconnu dès l'adresse : `router.url` vaut « / » avant la première navigation. */
  protected readonly homeAtStart = ['/', '/dashboard'].includes(window.location.pathname);
  protected readonly homeRow = computed(() => this.isHomePage() || (!this.pageSettled() && this.homeAtStart));

  /** Deuxième ligne vide : masquée, sauf pendant le premier affichage (voir `pageSettled`). */
  protected readonly secondaryBare = computed(() => !this.headerSearch() && !this.headerContent());
  protected readonly secondaryPending = computed(() =>
    this.secondaryBare() && !this.pageSettled() && this.secondaryExpectedAtStart);

  /**
   * L'overlay lui-même est monté à la racine (app.component.html) : rendu ici,
   * il restait sous la sidebar, `.content-header` et `.navigation` étant deux
   * stacking contexts frères. Cf. VoiceOverlayService.
   */
  openVoiceOverlay(): void {
    this.voiceOverlay.open();
  }

  notifications = this.notificationService.notifications;
  unreadCount = this.notificationService.unreadCount;

  // ─── Real-time clock ─────────────────────────────────────────────────────
  // Deux segments distincts : « mardi 1 sept. 26 » (capitalisé par le CSS)
  // et « 14h37 », séparés visuellement par un | et une icône horloge.
  currentDate = signal(this.formatDate());
  currentTime = signal(this.formatTime());

  private formatDate(): string {
    return new Date().toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    });
  }

  private formatTime(): string {
    const now = new Date();
    return `${now.getHours()}h${String(now.getMinutes()).padStart(2, '0')}`;
  }

  ngOnInit(): void {
    this.notificationService.refreshNotifications();

    // Première page affichée : ses actions sont posées, la deuxième ligne peut se replier.
    // L'en-tête n'apparaît qu'avec le profil : la première navigation peut être déjà finie.
    const settle = () => afterNextRender(() => this.pageSettled.set(true), { injector: this.injector });
    if (this.router.navigated) {
      settle();
    } else {
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        first(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(settle);
    }

    // Update notifications every 2 minutes
    interval(1000 * 120)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.notificationService.refreshNotifications());

    // Update clock every 15 seconds
    interval(1000 * 15)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentDate.set(this.formatDate());
        this.currentTime.set(this.formatTime());
      });
  }

  toggleDropdown(): void {
    this.showDropdown.update(v => !v);
  }

  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.showDropdown()) {
      this.showDropdown.set(false);
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe();
  }

  /** Même pattern que la page /notifications : retrait optimiste + undo 5 s. */
  /** Retire une notification de la liste et propose « Annuler » avant de l'effacer. */
  private removeNotificationWithUndo(id: number): void {
    const target = this.notifications().find(n => n.id === id);
    if (!target) return;

    this.notificationService.notifications.update(list => list.filter(n => n.id !== id));
    if (!target.is_read) this.notificationService.unreadCount.update(c => Math.max(0, c - 1));

    this.undoDeleteService.stage({
      label: 'Notification supprimée',
      execute: () => firstValueFrom(this.notificationService.deleteNotification(id)),
      // Réinsère celle-ci seulement : une notification arrivée entre-temps reste affichée.
      restore: () => {
        this.notificationService.notifications.update(list => list.some(n => n.id === id) ? list :
          [...list, target].sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()));
        if (!target.is_read) this.notificationService.unreadCount.update(c => c + 1);
      }
    });
  }

  deleteAllNotifications(): void {
    const snapshot = this.notifications();
    const unreadSnapshot = this.unreadCount();
    if (snapshot.length === 0) return;

    this.notificationService.notifications.set([]);
    this.notificationService.unreadCount.set(0);

    this.undoDeleteService.stage({
      label: 'Toutes les notifications supprimées',
      execute: () => firstValueFrom(this.notificationService.deleteAllNotifications()),
      restore: () => {
        this.notificationService.notifications.set(snapshot);
        this.notificationService.unreadCount.set(unreadSnapshot);
      }
    });
  }

  /**
   * Bouton « Supprimer » de la notification (survol / focus clavier) : même retrait optimiste
   * et annulation de 5 s que « Tout supprimer ».
   */
  deleteNotification(event: Event, notification: Notification): void {
    event.stopPropagation();
    const id = notification.id as number;
    const snapshot = this.notifications();
    const unreadSnapshot = this.unreadCount();
    this.notificationService.notifications.set(snapshot.filter(n => n.id !== id));
    if (!notification.is_read) this.notificationService.unreadCount.set(Math.max(0, unreadSnapshot - 1));

    this.undoDeleteService.stage({
      label: 'Notification supprimée',
      // La liste est déjà à jour : on supprime côté serveur sans repasser par le signal.
      execute: () => firstValueFrom(this.notificationService.deleteNotification(id)),
      restore: () => {
        this.notificationService.notifications.set(snapshot);
        this.notificationService.unreadCount.set(unreadSnapshot);
      }
    });
  }

  /** Une notification est un bouton : Entrée et Espace l'ouvrent comme un clic. */
  onNotificationKeydown(event: KeyboardEvent, notification: Notification): void {
    if (event.target !== event.currentTarget) return; // ne pas voler la frappe du bouton « Supprimer »
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onNotificationClick(notification);
    }
  }

  onNotificationClick(notification: Notification): void {
    // Un relâchement de swipe déclenche aussi un click : ne pas naviguer.
    if (this.suppressNotificationClick) {
      this.suppressNotificationClick = false;
      return;
    }

    if (!notification.is_read) {
      this.notificationService.markAsRead([notification.id as number]).subscribe();
    }

    if (notification.link) {
      this.router.navigateByUrl(notification.link);
      this.showDropdown.set(false);
    }
  }

  // ─── Swipe vers la droite pour supprimer une notification ────────────────
  // Aucun pattern de swipe n'existait dans l'app : geste pointer minimal,
  // convention CSS/signal (pas de DSL @angular/animations). Le mouvement doit
  // être franc et horizontal (seuil d'activation + |dx| > |dy|) pour ne voler
  // ni le clic ni le scroll vertical de la liste.

  private static readonly SWIPE_ACTIVATION_PX = 8;
  private static readonly SWIPE_DELETE_THRESHOLD_PX = 96;
  private static readonly SWIPE_LEAVE_MS = 200;

  swipedNotificationId = signal<number | null>(null);
  swipeDeltaX = signal(0);
  swipeDragging = signal(false);
  swipeLeavingId = signal<number | null>(null);

  private swipeCandidateId: number | null = null;
  private swipePointerId: number | null = null;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private suppressNotificationClick = false;

  onNotificationPointerDown(event: PointerEvent, notification: Notification): void {
    if (this.swipeLeavingId() !== null) return;
    this.suppressNotificationClick = false;
    this.swipeCandidateId = notification.id as number;
    this.swipePointerId = event.pointerId;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  onNotificationPointerMove(event: PointerEvent): void {
    if (this.swipeCandidateId === null || event.pointerId !== this.swipePointerId) return;

    const dx = event.clientX - this.swipeStartX;
    const dy = event.clientY - this.swipeStartY;

    if (!this.swipeDragging()) {
      if (Math.abs(dx) < HeaderComponent.SWIPE_ACTIVATION_PX || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) {
        // Mouvement vers la gauche : pas un geste de suppression, on abandonne.
        this.swipeCandidateId = null;
        this.swipePointerId = null;
        return;
      }
      this.swipeDragging.set(true);
      this.swipedNotificationId.set(this.swipeCandidateId);
      (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    }

    this.swipeDeltaX.set(Math.max(0, dx));
  }

  onNotificationPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.swipePointerId) return;

    const wasDragging = this.swipeDragging();
    const id = this.swipedNotificationId();
    const delta = this.swipeDeltaX();

    this.swipeCandidateId = null;
    this.swipePointerId = null;
    this.swipeDragging.set(false);

    if (!wasDragging) return;

    this.suppressNotificationClick = true;

    if (id !== null && delta >= HeaderComponent.SWIPE_DELETE_THRESHOLD_PX) {
      // Sortie par la droite, puis retrait annulable 5 s (MOKA-363) ; l'effacement réel
      // (RLS : delete own) part à la fin du délai.
      this.swipeLeavingId.set(id);
      this.swipeDeltaX.set(420);
      setTimeout(() => {
        this.resetSwipeState();
        this.removeNotificationWithUndo(id);
      }, HeaderComponent.SWIPE_LEAVE_MS);
    } else {
      this.resetSwipeState();
    }
  }

  onNotificationPointerCancel(event: PointerEvent): void {
    if (event.pointerId !== this.swipePointerId) return;
    this.swipeCandidateId = null;
    this.swipePointerId = null;
    this.swipeDragging.set(false);
    if (this.swipeLeavingId() === null) {
      this.resetSwipeState();
    }
  }

  private resetSwipeState(): void {
    this.swipedNotificationId.set(null);
    this.swipeDeltaX.set(0);
    this.swipeLeavingId.set(null);
  }

  swipeTransform(id: number | null | undefined): string | null {
    return this.swipedNotificationId() === id ? `translateX(${this.swipeDeltaX()}px)` : null;
  }

  isSwipeDragging(id: number | null | undefined): boolean {
    return this.swipeDragging() && this.swipedNotificationId() === id;
  }

  isSwipeLeaving(id: number | null | undefined): boolean {
    return this.swipeLeavingId() === id;
  }

  isSwipeDeleteArmed(id: number | null | undefined): boolean {
    return this.swipedNotificationId() === id
      && this.swipeDeltaX() >= HeaderComponent.SWIPE_DELETE_THRESHOLD_PX;
  }

  getModuleClass(module: NotificationModule): string {
    const moduleClasses: Record<NotificationModule, string> = {
      [NotificationModule.Calendrier]: 'module-calendrier',
      [NotificationModule.GestionProjet]: 'module-gestion-projet',
      [NotificationModule.ComiteSalaries]: 'module-comite-salaries',
      [NotificationModule.Inventaire]: 'module-inventaire',
      [NotificationModule.Admin]: 'module-admin',
      [NotificationModule.General]: 'module-general'
    };
    return moduleClasses[module] || 'module-general';
  }

  getIconName(icon: string): string {
    const iconMap: Record<string, string> = {
      'bell': 'iconoirBell',
      'chat-bubble': 'iconoirChatBubble',
      'calendar': 'iconoirCalendar',
      'user': 'iconoirUser',
      'check-circle': 'iconoirCheckCircle',
      'check': 'iconoirCheck',
      'edit': 'iconoirEdit',
      'package': 'iconoirPackage',
      'cursor-pointer': 'iconoirCursorPointer',
      'money-square': 'iconoirCash',
      'computer': 'iconoirSettings',
      'task': 'iconoirCheck',
      'trash': 'iconoirTrash'
    };
    return iconMap[icon] || 'iconoirBell';
  }

  getTimeAgo(date: Date | undefined | null): string {
    if (!date) return '';

    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now.getTime() - notifDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;

    return notifDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  }

  isDarkMode(): boolean {
    return this.designService.isDarkTheme();
  }

  toggleTheme(event?: Event): void {
    // L'événement sert d'origine à la goutte de café de la transition de thème.
    this.designService.toggleTheme(event);
  }

}

/** Voir `HeaderComponent.secondaryExpectedAtStart`. */
export function expectsSecondaryRow(path: string): boolean {
  const modules = ['/project-management', '/salaries', '/mokah-admin'];
  if (modules.some(m => path === m || path.startsWith(m + '/'))) return true;
  return path === '/admin' || path === '/materials' || path === '/materials/my-devices';
}
